import React from 'react';
import SEOHead, { faqSchema } from '@components/marketing/SEOHead';
import PageHead from '@components/marketing/PageHead';
import CtaBand from '@components/marketing/CtaBand';
import { ROLES } from './content';

const AUDIENCES = [
  {
    id: 'associations',
    name: 'Sports associations and federations',
    body: 'A state or national body running championships across affiliated units, with entries arriving from every district and results owed back to all of them. The office keeps one view of the whole calendar; each unit sees only its own members and fixtures.',
    points: ['Affiliated units register their own members', 'Championships by age group and category', 'Certificates and records held centrally'],
  },
  {
    id: 'corporate',
    name: 'Corporate leagues',
    body: 'An employer running an internal championship across offices, usually several sports at once over a few weeks, with colleagues entering in their own time and playing around a working day. Every office gets a coordinator; nobody needs to learn a sports admin system to take part.',
    points: ['One coordinator per office or location', 'Several sports running in parallel', 'Fixtures and results pushed to the people they affect'],
  },
  {
    id: 'institutions',
    name: 'Schools, colleges and academies',
    body: 'An inter-house or inter-college meet compressed into a few days, where the hard part is the schedule rather than the scale — many short matches, few courts, and students who are entered in more than one event.',
    points: ['Short slots and tight court rotation', 'Clash detection for students in several events', 'Participation certificates for everyone who played'],
  },
];

const FAQS = [
  ['Is SportsTMS only for large associations?',
   'No. The smallest useful season is one sport at one venue. The same system handles a national body across many states, because the difference between them is configuration rather than a different product.'],
  ['Can each unit or office manage its own people?',
   'Yes. A coordinator account is scoped to one city, office or affiliated unit. They register their own members, approve entries and appoint their own scorers, and they cannot see anybody else’s.'],
  ['Do participants need to install anything?',
   'Players use a mobile app to see their fixtures and results. Anyone who only wants to follow the competition can use the public website without an account or an install.'],
  ['Who owns the data?',
   'The association does. Each one runs on its own address with its own database, and nothing is shared between customers.'],
];

const WhoItsForPage = () => (
  <>
    <SEOHead
      title="Who it’s for"
      description="Built for sports associations, corporate leagues and institutions, with separate accounts for the league office, coordinators, scorers and players."
      schema={faqSchema(FAQS)}
    />

    <PageHead title="Built for whoever has to run the thing" image="/images/sportstms/colleagues.jpg">
          <p>
            The competitions differ; the job does not. Somebody takes the entries, somebody
            draws it, somebody keeps the score, and everybody wants to know where they play.
          </p>
    </PageHead>

    <section className="stms-sec">
      <div className="stms-wrap">
        <div className="stms-audiences">
          {AUDIENCES.map((a) => (
            <article key={a.id} id={a.id}>
              <h2>{a.name}</h2>
              <p>{a.body}</p>
              <ul>
                {a.points.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="stms-sec stms-sec--paper" id="roles">
      <div className="stms-wrap">
        <div className="stms-lede">
          <h2>The four accounts</h2>
          <p>Each one shows what that job needs and nothing else, so a whole season can run in parallel without anyone treading on anyone.</p>
        </div>
        <div className="stms-rolecards">
          {ROLES.map((r) => (
            <article key={r.who}>
              <h3>{r.who}</h3>
              <p className="stms-roles__scope">{r.scope}</p>
              <ul>
                {r.detail.map((d) => <li key={d}>{d}</li>)}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="stms-sec">
      <div className="stms-wrap">
        <div className="stms-lede"><h2>Common questions</h2></div>
        <dl className="stms-faq">
          {FAQS.map(([q, a]) => (
            <div key={q}><dt>{q}</dt><dd>{a}</dd></div>
          ))}
        </dl>
      </div>
    </section>

    <CtaBand title="Tell us what you run" image="/images/sportstms/podium.jpg" action="Book a demo">
      <p>Sports, venues and roughly how many people is enough for us to set something up.</p>
    </CtaBand>
  </>
);

export default WhoItsForPage;
